import { memo, useCallback } from "react";
import { generatePath } from "react-router";

import { cn } from "@/lib/chillnn/cn";
import dayjs from "@/lib/dayjs";
import { SalesMastType } from "@/lib/gql/__generated__/graphql";

import { PATHS } from "@/routes/paths";

import { Button } from "@/components/ui/button/button";
import { Dialog, DialogContent } from "@/components/ui/dialog/dialog";
import Article from "@/components/ui/icon/article";
import OpenInNew from "@/components/ui/icon/open-in-new";

import type { SearchResultTableRowData } from "../../../../../model/type/view-data";

type Props = {
	row: SearchResultTableRowData;
	open: boolean;
	onOpenChange: (open: boolean) => void;
};

const SectionWrapper = memo(({ title, children }: { title: string; children: React.ReactNode }) => {
	return (
		<div className="col-span-1 space-y-2">
			<p className="text-[15px] font-semibold">{title}</p>
			<div className="space-y-1">{children}</div>
		</div>
	);
});

const KVItem = memo(({ title, value, className }: { title: string; value: string; className?: string }) => {
	return (
		<div className={cn("flex flex-wrap justify-between text-sm", className)}>
			<p>{title}</p>
			<p>{value}</p>
		</div>
	);
});

export const SearchResultTableTBodyPopover = ({ row, open, onOpenChange }: Props) => {
	const {
		hotelID,

		salesMastType,
		transferredAt,

		income,
		commission,
		totalIncome,
		cardBrand,
	} = row;

	const totalIncomeText = `${totalIncome.toLocaleString()}円`;
	const commissionText =
		commission !== null && commission !== undefined && commission !== 0
			? `-${commission.toLocaleString() ?? 0}円`
			: "";
	const incomeText = `${income.toLocaleString()}円`;

	const activateModal = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();
			onOpenChange(true);
		},
		[onOpenChange],
	);

	const handleClickNavigateButton = useCallback(() => {
		const open = (path: string) => window.open(path, "_blank");

		if (salesMastType === SalesMastType.Ec) {
			const path = generatePath(PATHS.HOTEL.MANAGEMENT.EC_SALES.DETAIL, {
				hotelID,
				ecID: row.userECReceiptID,
			});
			open(path);
		}

		if (salesMastType === SalesMastType.ReservationCredit || salesMastType === SalesMastType.ReservationLocal) {
			const path = generatePath(PATHS.HOTEL.MANAGEMENT.RESERVATION.DETAIL, {
				hotelID,
				reservationID: row.reservationID,
			});
			open(path);
		}
	}, [hotelID, row, salesMastType]);

	return (
		<>
			<div onClick={(e) => e.stopPropagation()}>
				<Dialog open={open} onOpenChange={onOpenChange}>
					<DialogContent className="max-w-2xl">
						<div className="grid  grid-cols-2 gap-x-10 p-3">
							<SectionWrapper title="決済情報">
								{cardBrand && <KVItem title={"カードブランド"} value={cardBrand} />}
								<KVItem title={"決済日"} value={dayjs(transferredAt).format("YYYY-MM-DD HH:mm")} />
								<KVItem title={"決済料金"} value={totalIncomeText} />
								{commissionText && <KVItem title={"手数料"} value={commissionText} />}
								<KVItem
									title={"収支合計"}
									value={incomeText}
									className="mt-1 border-t-2 border-t-zinc-300 pt-1"
								/>
							</SectionWrapper>
							{(salesMastType === SalesMastType.Ec ||
								salesMastType === SalesMastType.ReservationCredit) && (
								<SectionWrapper title="購入者情報">
									<KVItem title={"氏名"} value={row.guestName} />
									<KVItem title={"電話番号"} value={row.guestTel} />
									<KVItem title={"住所"} value={row.guestAddress} />
									<KVItem title={"メール"} value={row.guestEmail} />
									<Button
										onClick={handleClickNavigateButton}
										type="button"
										variant="outline"
										size="sm"
										className="flex items-center gap-x-2"
									>
										詳細
										<OpenInNew size="sm" />
									</Button>
								</SectionWrapper>
							)}
							{(salesMastType === SalesMastType.TicketBasic ||
								salesMastType === SalesMastType.TicketPro) && (
								<SectionWrapper title="利用プラン情報">
									<KVItem title={"プラン名"} value={row.ticketName} />
									<KVItem title={"契約期間"} value={row.validDtEnd} />
								</SectionWrapper>
							)}
						</div>
					</DialogContent>
				</Dialog>
			</div>

			<Button
				onClick={activateModal}
				type="button"
				variant="outline"
				size="sm"
				className="flex items-center gap-x-1 whitespace-nowrap"
			>
				<Article size="sm" />
				詳細を見る
			</Button>
		</>
	);
};

SearchResultTableTBodyPopover.displayName = "SearchResultTableTBodyPopover";
